import {
  CloseCircleFilled,
  EditOutlined,
  PlusOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { debounce } from "lodash";
import {
  ActionButton,
  INA,
  TableLayout,
  TableLayoutFooter,
  TableContent,
  getTableShowingRange,
} from "components/table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ModalStatus } from "types/modal";
import { QueryParam } from "types/query";
import { Staff } from "types/staff";
import { getTitle } from "utils";
import { $url } from "utils/url";
import { ResetPasswordModal } from "./components/ResetPasswordModal";
import { ReceptionistModal } from "./components/ReceptionistModal";
import { receptionistApi } from "api/receptionist.api";

export const ReceptionistPage = ({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Staff[]>([]);
  const [total, setTotal] = useState(0);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Partial<Staff>>({});
  const [modalStatus, setModalStatus] = useState<ModalStatus>("create");
  const [visibleResetPasswordModal, setVisibleResetPasswordModal] =
    useState(false);

  const debounceSearch = useCallback(
    debounce(
      (keyword: string) =>
        setQuery({ ...query, search: keyword, page: 1, limit: 20 }),
      300,
    ),
    [setQuery, query],
  );

  useEffect(() => {
    document.title = getTitle(title);
  }, []);

  useEffect(() => {
    fetchData();
  }, [query]);

  const fetchData = async () => {
    setLoading(true);
    const res = await receptionistApi.findAll(query);
    setLoading(false);
    setData(res.data.receptionists);
    setTotal(res.data.total);
  };


  const columns = useMemo<TableColumnsType<Staff>>(
    () => [
      {
        title: "Username",
        dataIndex: "username",
        key: "username",
        width: 260,
        render: (_, record) => (
          <INA
            image={record.avatar ? $url(record.avatar) : undefined}
            name={record.username || "--"}
            additional={record.name || "--"}
          />
        ),
      },
      {
        title: "Họ tên",
        dataIndex: "name",
        key: "name",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Số điện thoại",
        dataIndex: "phone",
        key: "phone",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Quyền",
        key: "role",
        width: 180,
        render: (_, record) => <TableContent>{record.role?.name || "--"}</TableContent>,
      },
      {
        title: "Thao tác",
        key: "action",
        width: 100,
        align: "center",
        render: (_, record) => (
          <div className="flex gap-2 justify-center items-center">
            <Tooltip title="Cập nhật">
              <ActionButton
                aria-label="Cập nhật"
                onClick={() => {
                  setSelectedStaff(record);
                  setVisibleModal(true);
                  setModalStatus("update");
                }}
              >
                <EditOutlined style={{ color: "#6c84e5" }} />
              </ActionButton>
            </Tooltip>
            <Tooltip title="Reset mật khẩu">
              <ActionButton
                aria-label="Reset mật khẩu"
                onClick={() => {
                  setSelectedStaff(record);
                  setVisibleResetPasswordModal(true);
                }}
              >
                <KeyOutlined style={{ color: "#2563eb" }} />
              </ActionButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [],
  );

  const { showingFrom, showingTo } = getTableShowingRange(
    query.page,
    query.limit,
    total,
  );

  return (
    <div>
      <TableLayout
          filter={
            <AppTableToolbar
              search={{
                value: search,
                placeholder: "Tìm tên, số điện thoại",
                onChange: (value) => {
                  setSearch(value);
                  debounceSearch(value);
                },
                inputProps: {
                  allowClear: {
                    clearIcon: (
                      <CloseCircleFilled
                        onClick={() => {
                          setSearch("");
                          setQuery((prev) => ({ ...prev, page: 1, search: "" }));
                        }}
                      />
                    ),
                  },
                },
              }}
              leftActions={
                <AppButton
                  tone="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setVisibleModal(true);
                    setModalStatus("create");
                    setSelectedStaff({});
                  }}
                >
                  Tạo mới
                </AppButton>
              }
              filterLabel={null}
            />
          }
          table={
            <AppTable<Staff>
              rowKey="id"
              loading={loading}
              pagination={false}
              dataSource={data}
              columns={columns}
              scroll={{ x: 1050 }}
            />
          }
          footer={
            <TableLayoutFooter
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={total}
              currentPage={query.page}
              pageSize={query.limit}
              onChange={({ limit, page }) => {
                setQuery((prev) => ({ ...prev, page, limit }));
              }}
            />
          }
        />

      <ResetPasswordModal
        onSubmitOk={() => {}}
        status={"create"}
        staffId={selectedStaff.id || 0}
        visible={visibleResetPasswordModal}
        onClose={() => {
          setVisibleResetPasswordModal(false);
        }}
      />

      <ReceptionistModal
        onSubmitOk={fetchData}
        onClose={() => setVisibleModal(false)}
        visible={visibleModal}
        staff={selectedStaff}
        status={modalStatus}
      />
    </div>
  );
};
